import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowRight, CheckCircle2, FileText, Database, Sparkles, LayoutDashboard } from "lucide-react";
import Layout from "@/components/Layout";
import { RegulensMark } from "@/components/RegulensMark";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const TRUST_LINKS = [
  { to: "/methodology", label: "Methodology", icon: FileText },
  { to: "/sample-report", label: "Sample Report", icon: Sparkles },
  { to: "/data-verification", label: "Data Verification", icon: Database },
  { to: "/legal-review-process", label: "Legal Review", icon: Shield },
];

// Landing page CTA — different states for anonymous vs signed-in visitors.
// Anonymous  → two-path acquisition CTA (Explore Demo / Start Free Audit).
// Signed-in  → "Welcome back" panel with a direct Dashboard link and a
//              secondary "Run a new audit" text link. The homepage stays
//              reachable on purpose (paying customers may still land here
//              from marketing links, colleagues, etc.) but the CTA is
//              personalized so it never re-pitches the acquisition funnel
//              to an existing customer.
const TIER_LABEL = {
  free: "Free tier",
  solo: "Solo tier",
  growth: "Growth tier",
  professional: "Professional tier",
  agency: "Agency tier",
};

export default function Landing() {
  const { user } = useAuth();
  const firstName = user?.email ? user.email.split("@")[0].split(/[._-]/)[0] : "";
  const displayName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : "";
  const tierLabel = TIER_LABEL[user?.tier] || null;

  return (
    <Layout>
      {/* HERO */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        {/* Ambient RegulensMark backdrop — HOMEPAGE-ONLY (do not propagate).
            Fully contained within the hero section (no negative right/top
            offsets that push past the viewport). Two-layer render:
              1. Faded sage lens rings at opacity-30 (backdrop feel).
              2. Solid gold dot at full opacity so it reads as true gold,
                 not washed-out amber. The dot is a tiny percentage of the
                 total mark area, so keeping it opaque doesn't fight the
                 hero copy on the left. */}
        <div className="absolute right-0 top-4 w-[min(560px,45%)] pointer-events-none" aria-hidden="true">
          <div className="opacity-30">
            <RegulensMark size={560} strokeWidth={0.35} className="w-full h-auto" />
          </div>
          {/* Second-pass render: opaque gold dot only. Same geometry as
              RegulensMark ("full" variant: cx=16, cy=16, r=2.6 over a
              32-unit viewBox), so it lands exactly on top of the faded
              dot below. */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <circle cx="16" cy="16" r="2.6" fill="var(--rg-gold)" />
          </svg>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--rg-bg)] via-[var(--rg-bg)]/85 to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--rg-border)] bg-[var(--rg-surface)] text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--rg-sage)] mb-6" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--rg-sage)] animate-pulse" />
              50-Jurisdiction Tax Coverage · 15-State Privacy Scoring
            </div>
            <h1 className="font-heading font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-[var(--rg-text-strong)]">
              Verified U.S. Shopify{" "}
              <span className="text-[var(--rg-sage)]">Compliance Intelligence.</span>
            </h1>
            <p className="mt-6 text-lg text-[var(--rg-text)] leading-relaxed max-w-2xl">
              Every finding cites an official government source. Every readiness score uses verified state statutes. No third-party summaries, no fabricated thresholds — just informational readiness indicators for serious U.S. merchants.
            </p>
          </motion.div>

          {/* CTA — personalizes for signed-in users; two-path acquisition
              funnel for anonymous visitors. See TIER_LABEL comment above. */}
          {user ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-12 max-w-2xl"
              data-testid="landing-welcome-back"
            >
              <div className="rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-7">
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3" data-testid="welcome-back-kicker">
                  Welcome back{tierLabel ? ` · ${tierLabel}` : ""}
                </div>
                <div className="font-heading font-extrabold text-2xl text-[var(--rg-text-strong)] mb-2" data-testid="welcome-back-heading">
                  {displayName ? `Hi ${displayName} — your dashboard is ready.` : "Your dashboard is ready."}
                </div>
                <p className="text-sm text-[var(--rg-text)] mb-5">
                  Pick up where you left off with your latest readiness score, jurisdiction breakdown, and remediation checklist.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link to="/dashboard" data-testid="welcome-back-dashboard">
                    <Button className="bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      Open your dashboard
                    </Button>
                  </Link>
                  <Link
                    to="/onboarding"
                    className="text-sm text-[var(--rg-text)] hover:text-[var(--rg-text-strong)] underline underline-offset-4"
                    data-testid="welcome-back-new-audit"
                  >
                    Or run a new audit →
                  </Link>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-12 grid md:grid-cols-2 gap-5 max-w-4xl"
              data-testid="landing-two-path-cta"
            >
              <Link to="/demo" className="group" data-testid="cta-explore-demo">
                <div className="h-full rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-7 hover:border-[var(--rg-border-hi)] hover:bg-[var(--rg-surface-2)] transition-fast">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-muted)] mb-3">Path 1 · No Account Needed</div>
                  <div className="font-heading font-extrabold text-2xl text-[var(--rg-text-strong)] mb-2 flex items-center gap-2">
                    Explore Demo Report
                    <ArrowRight className="w-5 h-5 text-[var(--rg-sage)] transition-transform group-hover:translate-x-1" />
                  </div>
                  <p className="text-sm text-[var(--rg-text)]">
                    See a real compliance report for a sample Shopify store in under 30 seconds. No signup required.
                  </p>
                </div>
              </Link>

              <Link to="/register" className="group" data-testid="cta-audit-real-store">
                <div className="h-full rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-7 hover:bg-[var(--rg-sage-soft)] transition-fast relative overflow-hidden">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Path 2 · Verified Audit</div>
                  <div className="font-heading font-extrabold text-2xl text-[var(--rg-text-strong)] mb-2 flex items-center gap-2">
                    Start Free Audit
                    <ArrowRight className="w-5 h-5 text-[var(--rg-sage)] transition-transform group-hover:translate-x-1" />
                  </div>
                  <p className="text-sm text-[var(--rg-text)]">
                    Verified store owners get one free compliance audit. Upgrade for full access and ongoing monitoring.
                  </p>
                </div>
              </Link>
            </motion.div>
          )}

          {/* TRUST SIGNALS */}
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[var(--rg-text)]" data-testid="trust-signals">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--rg-sage)]" /> 50 Tax Jurisdictions | 15 Privacy-Scored States | Informational Readiness Indicators Only</div>
          </div>
          <p className="mt-4 text-xs text-[var(--rg-muted)] max-w-2xl leading-relaxed" data-testid="readiness-subtext">
            Regulens provides general informational readiness content only. Nothing on this platform constitutes legal advice.
          </p>

          {/* TRUST LINKS */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="trust-links">
            {TRUST_LINKS.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--rg-border)] text-xs text-[var(--rg-text)] hover:border-[var(--rg-sage)] hover:text-[var(--rg-text-strong)] transition-fast" data-testid={`trust-link-${label.toLowerCase().replace(/\s/g, '-')}`}>
                <Icon className="w-3 h-3" /> {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* JURISDICTION COVERAGE COPY */}
      <section className="relative py-20 border-t border-[var(--rg-border)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">What we cover</div>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-6 max-w-4xl" data-testid="coverage-heading">
            50 tax jurisdictions. 15 states with active privacy scoring. Every data point traceable to an official government source.
          </h2>
          <p className="text-[var(--rg-text)] text-lg leading-relaxed max-w-4xl" data-testid="coverage-body">
            Sales tax nexus coverage spans 50 U.S. jurisdictions (48 states + DC). Active numeric privacy law scoring covers 15 states including California, Colorado, Virginia, Illinois, Washington, Texas, New York, Tennessee, and 7 more. Every data point traces to an official state revenue department or attorney general publication.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 border-t border-[var(--rg-border)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Built for serious merchants</div>
              <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-6">
                Compliance intelligence, not compliance theater.
              </h2>
              <p className="text-[var(--rg-text)] leading-relaxed mb-8">
                Regulens scores four pillars — sales tax nexus, privacy law readiness, policy documents, and exposure awareness — against verified state statutes. You always see why a score is what it is, with the official government citation right next to it.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/register" data-testid="features-cta-register">
                  <Button className="bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold">Start Free Audit <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </Link>
                <Link to="/sample-report" data-testid="features-cta-sample">
                  <Button variant="outline" className="border-[var(--rg-border)] bg-transparent text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]">See Sample Report</Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {[
                { title: "Verified Source Proof on every score", body: "Authority name, official URL, statute citation, last-verified date — all in plain view." },
                { title: "NY dual-threshold handled correctly", body: "$500K revenue AND 100+ transactions. We don't get this wrong like most calculators do." },
                { title: "Caching + rate limits keep costs predictable", body: "Free tier: 1 fresh audit / 30 days. Pro: 5. Same inputs = cached result, no AI call." },
                { title: "Real-time projected score calculator", body: "Check off remediation items and watch your score rise before you run a new audit." },
              ].map((f, i) => (
                <div key={f.title} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 hover:border-[var(--rg-border-hi)] transition-fast" data-testid={`feature-${i}`}>
                  <div className="text-[var(--rg-text-strong)] font-semibold mb-1">{f.title}</div>
                  <div className="text-sm text-[var(--rg-text)]">{f.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
